import { Component, Inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { LocalizationService } from '../../core/i18n/localization.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { PL_DASH_DATE_FORMATS, PlDateAdapter } from '../../core/utils/pl-date-adapter';
import { InMemoryDataService } from '../../core/data/in-memory-data.service';
import { CollectionKey } from '../../core/models/domain.models';

export interface FilterSelectOption {
  value: number | string;
  label: string;
}

export interface FilterFieldConfig {
  value: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select';
  options?: FilterSelectOption[];
}

export interface FilterDialogData {
  status: string;
  columnFilters: Record<string, string>;
  apiFilters: Record<string, string>;
  dateFrom: string;
  dateTo: string;
  studentId: number | '';
  instructorId: number | '';
  categoryId: number | string | '';
  entityKey: string;
  title: string;
  filterFields: FilterFieldConfig[];
  apiFilterFields: FilterFieldConfig[];
  statusOptions: string[];
  studentOptions: FilterSelectOption[];
  instructorOptions: FilterSelectOption[];
  categoryOptions: FilterSelectOption[];
}

@Component({
  selector: 'app-filter-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    TranslatePipe
  ],
  providers: [
    { provide: DateAdapter, useClass: PlDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PL_DASH_DATE_FORMATS }
  ],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="filter-form" [class.wide-layout]="useWideLayout()">
        @for (field of data.apiFilterFields; track field.value) {
          @if (field.type === 'date') {
            <mat-form-field>
              <mat-label>{{ field.label }}</mat-label>
              <input matInput [matDatepicker]="apiPicker" [formControlName]="apiControlName(field.value)" />
              <mat-datepicker-toggle matIconSuffix [for]="apiPicker" />
              <mat-datepicker #apiPicker />
            </mat-form-field>
          } @else {
            <mat-form-field>
              <mat-label>{{ field.label }}</mat-label>
              @if (field.type === 'select') {
              <input
                matInput
                [formControlName]="apiControlName(field.value)"
                [matAutocomplete]="apiAuto"
                [placeholder]="'common.any' | t"
                #apiTrigger="matAutocompleteTrigger"
                (focus)="apiTrigger.openPanel()"
                (click)="apiTrigger.openPanel()"
                (input)="loadRemoteOptions(field, $any($event.target).value)"
              />
              <mat-autocomplete #apiAuto="matAutocomplete">
                <mat-option value="">{{ 'common.any' | t }}</mat-option>
                @for (option of optionsFor(field, apiControlName(field.value)); track option.value) {
                  <mat-option [value]="option.label">{{ option.label }}</mat-option>
                }
              </mat-autocomplete>
              } @else {
                <input
                  matInput
                  [type]="field.type"
                  [formControlName]="apiControlName(field.value)"
                  [attr.inputmode]="isPeselField(field) ? 'numeric' : null"
                  [attr.maxlength]="isPeselField(field) ? 11 : null"
                  [attr.autocomplete]="isPeselField(field) ? 'off' : null"
                  [attr.data-private]="isPeselField(field) ? 'true' : null"
                  (input)="onPrivatePeselInput(field, $event)"
                />
                @if (isPeselField(field) && peselError()) {
                  <mat-error>{{ 'validation.invalidPesel' | t }}</mat-error>
                }
              }
            </mat-form-field>
          }
        }
        @for (field of data.filterFields; track field.value) {
          @if (field.type === 'date') {
            <mat-form-field>
              <mat-label>{{ field.label }}</mat-label>
              <input matInput [matDatepicker]="columnPicker" [formControlName]="columnControlName(field.value)" />
              <mat-datepicker-toggle matIconSuffix [for]="columnPicker" />
              <mat-datepicker #columnPicker />
            </mat-form-field>
          } @else {
            <mat-form-field>
              <mat-label>{{ field.label }}</mat-label>
              @if (field.type === 'select') {
              <input
                matInput
                [formControlName]="columnControlName(field.value)"
                [matAutocomplete]="columnAuto"
                [placeholder]="'common.any' | t"
                #columnTrigger="matAutocompleteTrigger"
                (focus)="columnTrigger.openPanel()"
                (click)="columnTrigger.openPanel()"
                (input)="loadRemoteOptions(field, $any($event.target).value)"
              />
              <mat-autocomplete #columnAuto="matAutocomplete">
                <mat-option value="">{{ 'common.any' | t }}</mat-option>
                @for (option of optionsFor(field, columnControlName(field.value)); track option.value) {
                  <mat-option [value]="option.label">{{ option.label }}</mat-option>
                }
              </mat-autocomplete>
              } @else {
                <input matInput [type]="field.type" [formControlName]="columnControlName(field.value)" />
              }
            </mat-form-field>
          }
        }
        @if (data.statusOptions.length > 0) {
          <mat-form-field>
            <mat-label>{{ 'common.status' | t }}</mat-label>
            <mat-select formControlName="status">
              <mat-option value="">{{ 'common.any' | t }}</mat-option>
              @for (status of data.statusOptions; track status) {
                <mat-option [value]="status">{{ translateStatus(status) }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
        @if (showLegacyLessonFilters()) {
          <mat-form-field>
            <mat-label>{{ 'common.from' | t }}</mat-label>
            <input matInput [matDatepicker]="dateFromPicker" formControlName="dateFrom" />
            <mat-datepicker-toggle matIconSuffix [for]="dateFromPicker" />
            <mat-datepicker #dateFromPicker />
          </mat-form-field>
          <mat-form-field>
            <mat-label>{{ 'common.to' | t }}</mat-label>
            <input matInput [matDatepicker]="dateToPicker" formControlName="dateTo" />
            <mat-datepicker-toggle matIconSuffix [for]="dateToPicker" />
            <mat-datepicker #dateToPicker />
          </mat-form-field>
          <mat-form-field>
            <mat-label>{{ 'common.student' | t }}</mat-label>
            <mat-select formControlName="studentId">
              <mat-option value="">{{ 'common.any' | t }}</mat-option>
              @for (item of data.studentOptions; track item.value) {
                <mat-option [value]="item.value">{{ item.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field>
            <mat-label>{{ 'common.instructor' | t }}</mat-label>
            <mat-select formControlName="instructorId">
              <mat-option value="">{{ 'common.any' | t }}</mat-option>
              @for (item of data.instructorOptions; track item.value) {
                <mat-option [value]="item.value">{{ item.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field class="full-row">
            <mat-label>{{ 'common.category' | t }}</mat-label>
            <mat-select formControlName="categoryId">
              <mat-option value="">{{ 'common.any' | t }}</mat-option>
              @for (item of data.categoryOptions; track item.value) {
                <mat-option [value]="item.value">{{ item.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="clear()">{{ 'common.clear' | t }}</button>
      <button mat-button (click)="close()">{{ 'common.cancel' | t }}</button>
      <button mat-flat-button color="primary" (click)="apply()">{{ 'common.apply' | t }}</button>
    </mat-dialog-actions>
  `,
  styles: `
    .filter-form {
      display: grid;
      gap: 12px;
      min-width: 280px;
      grid-template-columns: minmax(0, 1fr);
      grid-auto-rows: min-content;
      align-content: start;
      align-items: start;
    }

    .filter-form.wide-layout {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .filter-form mat-form-field {
      width: 100%;
    }

    .full-row {
      grid-column: 1 / -1;
    }

    @media (max-width: 600px) {
      .filter-form.wide-layout {
        grid-template-columns: minmax(0, 1fr);
      }

      .full-row {
        grid-column: auto;
      }
    }
  `
})
export class FilterDialogComponent {
  readonly form;
  readonly peselError = signal(false);

  constructor(
    private readonly fb: FormBuilder,
    private readonly localization: LocalizationService,
    private readonly dataService: InMemoryDataService,
    private readonly dialogRef: MatDialogRef<FilterDialogComponent, FilterDialogData | null>,
    @Inject(MAT_DIALOG_DATA) readonly data: FilterDialogData
  ) {
    this.form = this.fb.nonNullable.group({
      status: this.data.status,
      ...Object.fromEntries(
        this.data.apiFilterFields.map((field) => [
          this.apiControlName(field.value),
          field.type === 'date'
            ? this.toDateValue(this.data.apiFilters?.[`${field.value}`])
            : this.formValueForField(field, this.data.apiFilters?.[`${field.value}`])
        ])
      ),
      ...Object.fromEntries(
        this.data.filterFields.map((field) => [
          this.columnControlName(field.value),
          field.type === 'date'
            ? this.toDateValue(this.data.columnFilters?.[`${field.value}`])
            : this.formValueForField(field, this.data.columnFilters?.[`${field.value}`])
        ])
      ),
      dateFrom: this.toDateValue(this.data.dateFrom),
      dateTo: this.toDateValue(this.data.dateTo),
      studentId: this.data.studentId,
      instructorId: this.data.instructorId,
      categoryId: this.data.categoryId
    });
  }

  useWideLayout(): boolean {
    return this.data.apiFilterFields.length + this.data.filterFields.length > 3;
  }

  showLegacyLessonFilters(): boolean {
    return this.data.entityKey === 'lessons' && this.data.apiFilterFields.length === 0;
  }

  columnControlName(value: number | string): string {
    return `column_${value}`;
  }

  apiControlName(value: number | string): string {
    return `api_${value}`;
  }

  translateStatus(status: string): string {
    const translated = this.localization.translate(`status.${status}`);
    if (translated !== `status.${status}`) {
      return translated;
    }
    const legacy = this.localization.translate(`common.${status}`);
    return legacy === `common.${status}` ? status : legacy;
  }

  filteredOptions(options: FilterSelectOption[], controlName: string): FilterSelectOption[] {
    const query = `${this.form.get(controlName)?.value ?? ''}`.trim().toLowerCase();
    if (!query) {
      return options;
    }
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }

  private readonly remoteOptions = new Map<string, FilterSelectOption[]>();
  private readonly remoteSearchTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly remoteSearchQueries = new Map<string, string>();

  optionsFor(field: FilterFieldConfig, controlName: string): FilterSelectOption[] {
    return this.filteredOptions(this.remoteOptions.get(field.value) ?? field.options ?? [], controlName);
  }

  loadRemoteOptions(field: FilterFieldConfig, value: unknown): void {
    const collection = this.remoteCollectionForField(field.value);
    if (!collection) {
      return;
    }
    const query = `${value ?? ''}`.trim();
    const existingTimer = this.remoteSearchTimers.get(field.value);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    if (!query) {
      this.remoteOptions.delete(field.value);
      this.remoteSearchQueries.delete(field.value);
      return;
    }
    this.remoteSearchQueries.set(field.value, query);
    this.remoteSearchTimers.set(field.value, setTimeout(() => {
      this.dataService.fetchCollectionPage$(collection, { page: 1, pageSize: 50, filters: { query } }).subscribe({
        next: (page) => {
          if (this.remoteSearchQueries.get(field.value) !== query) {
            return;
          }
          this.remoteOptions.set(field.value, page.items.map((item) => {
            const row = item as unknown as Record<string, unknown> & { id: string | number };
            return {
              value: row.id,
              label: collection === 'students' || collection === 'instructors'
                ? `${row['name'] ?? ''} ${row['surname'] ?? ''}`.trim()
                : `${row['name'] ?? ''}`.trim()
            };
          }).filter((option) => option.label.length > 0));
        }
      });
    }, 250));
  }

  private remoteCollectionForField(field: string): CollectionKey | null {
    if (field === 'studentId' || field === 'availableForStudentId') {
      return 'students';
    }
    if (field === 'instructorId' || field === 'availableForInstructorId') {
      return 'instructors';
    }
    if (field === 'courseInstanceId' || field === 'availableForCourseInstanceId') {
      return 'courseInstances';
    }
    if (field === 'resourceId' || field === 'availableForResourceId') {
      return 'resources';
    }
    return null;
  }

  clear(): void {
    this.form.setValue({
      status: '',
      ...Object.fromEntries(this.data.apiFilterFields.map((field) => [this.apiControlName(field.value), ''])),
      ...Object.fromEntries(this.data.filterFields.map((field) => [this.columnControlName(field.value), ''])),
      dateFrom: '',
      dateTo: '',
      studentId: '',
      instructorId: '',
      categoryId: ''
    });
  }

  close(): void {
    this.dialogRef.close(null);
  }

  apply(): void {
    const peselControl = this.form.get(this.apiControlName('personalIdentificationNumber'));
    const pesel = `${peselControl?.value ?? ''}`.trim();
    if (pesel && !this.isValidPesel(pesel)) {
      this.peselError.set(true);
      peselControl?.markAsTouched();
      return;
    }
    const rawValue = this.form.getRawValue() as Record<string, unknown>;
    this.dialogRef.close({
      status: `${rawValue['status'] ?? ''}`,
      apiFilters: Object.fromEntries(
        this.data.apiFilterFields.map((field) => [
          `${field.value}`,
          field.type === 'date'
            ? this.toIsoDate(rawValue[this.apiControlName(field.value)])
            : this.valueForField(field, rawValue[this.apiControlName(field.value)])
        ])
      ),
      dateFrom: this.toIsoDate(rawValue['dateFrom']),
      dateTo: this.toIsoDate(rawValue['dateTo']),
      studentId: (rawValue['studentId'] as number | '') ?? '',
      instructorId: (rawValue['instructorId'] as number | '') ?? '',
      categoryId: (rawValue['categoryId'] as number | string | '') ?? '',
      columnFilters: Object.fromEntries(
        this.data.filterFields.map((field) => [
          `${field.value}`,
          field.type === 'date'
            ? this.toIsoDate(rawValue[this.columnControlName(field.value)])
            : this.valueForField(field, rawValue[this.columnControlName(field.value)])
        ])
      ),
      entityKey: this.data.entityKey,
      title: this.data.title,
      filterFields: this.data.filterFields,
      apiFilterFields: this.data.apiFilterFields,
      statusOptions: this.data.statusOptions,
      studentOptions: this.data.studentOptions,
      instructorOptions: this.data.instructorOptions,
      categoryOptions: this.data.categoryOptions
    });
  }

  isPeselField(field: FilterFieldConfig): boolean {
    return field.value === 'personalIdentificationNumber';
  }

  onPrivatePeselInput(field: FilterFieldConfig, event: Event): void {
    if (!this.isPeselField(field)) return;
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 11);
    if (input.value !== digits) {
      input.value = digits;
      this.form.get(this.apiControlName(field.value))?.setValue(digits);
    }
    this.peselError.set(false);
  }

  private isValidPesel(value: string): boolean {
    if (!/^\d{11}$/.test(value)) return false;
    const digits = [...value].map(Number);
    const checksum = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3]
      .reduce((sum, weight, index) => sum + weight * digits[index], 0);
    if ((10 - (checksum % 10)) % 10 !== digits[10]) return false;

    const year = Number(value.slice(0, 2));
    const encodedMonth = Number(value.slice(2, 4));
    const day = Number(value.slice(4, 6));
    const century = encodedMonth >= 81 ? 1800 : encodedMonth >= 61 ? 2200 : encodedMonth >= 41 ? 2100 : encodedMonth >= 21 ? 2000 : 1900;
    const monthOffset = encodedMonth >= 81 ? 80 : encodedMonth >= 61 ? 60 : encodedMonth >= 41 ? 40 : encodedMonth >= 21 ? 20 : 0;
    const month = encodedMonth - monthOffset;
    const date = new Date(century + year, month - 1, day);
    return date.getFullYear() === century + year && date.getMonth() === month - 1 && date.getDate() === day;
  }

  private toDateValue(value: unknown): Date | string {
    if (!value) {
      return '';
    }
    if (value instanceof Date) {
      return value;
    }
    const text = `${value}`.trim();
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (isoMatch) {
      const [, yearRaw, monthRaw, dayRaw] = isoMatch;
      return new Date(Number(yearRaw), Number(monthRaw) - 1, Number(dayRaw));
    }
    const dashMatch = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(text);
    if (dashMatch) {
      const [, dayRaw, monthRaw, yearRaw] = dashMatch;
      return new Date(Number(yearRaw), Number(monthRaw) - 1, Number(dayRaw));
    }
    return text;
  }

  private formValueForField(field: FilterFieldConfig, value: unknown): string {
    if (field.type !== 'select') {
      return `${value ?? ''}`;
    }
    return this.optionLabelForValue(field.options ?? [], value);
  }

  private valueForField(field: FilterFieldConfig, value: unknown): string {
    if (field.type !== 'select') {
      return `${value ?? ''}`.trim();
    }
    return `${this.valueForOptions(this.remoteOptions.get(field.value) ?? field.options ?? [], value)}`;
  }

  private valueForOptions(options: FilterSelectOption[], value: unknown): string | number | '' {
    const text = `${value ?? ''}`.trim();
    if (!text) {
      return '';
    }
    const exact = options.find((option) => option.label === text || `${option.value}` === text);
    return exact?.value ?? '';
  }

  private optionLabelForValue(options: FilterSelectOption[], value: unknown): string {
    const text = `${value ?? ''}`.trim();
    if (!text) {
      return '';
    }
    return options.find((option) => option.label === text || `${option.value}` === text)?.label ?? '';
  }

  private toIsoDate(value: unknown): string {
    if (!value) {
      return '';
    }
    if (value instanceof Date && Number.isFinite(value.getTime())) {
      return [
        value.getFullYear(),
        String(value.getMonth() + 1).padStart(2, '0'),
        String(value.getDate()).padStart(2, '0')
      ].join('-');
    }
    return `${value}`.trim();
  }
}
